import { PlusCircle } from 'feather-icons-react/build/IconComponents'
import React, { useState, useEffect, useCallback } from 'react'
import Select from 'react-select'
import { useToast } from '../../../Toaster/ToastContext'
import PropTypes from 'prop-types';
import { apiClient } from '../../../services/apiClient';

export default function AddUsers({ user, onSave }) {
    const { showToast } = useToast();
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [branches, setBranches] = useState([
        { value: '', label: 'Choose' }
    ]);
    const [roles, setRoles] = useState([
        { value: '', label: 'Choose' }
    ]);

    const [formData, setFormData] = useState({
        name: '',
        user_image: '',
        phone: '',
        email: '',
        role_id: '',
        branch_id: '',
        status: 'active',
        password: '',
        password_confirmation:''
    });

    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePassword = () => {
      setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const [showConfirmPassword, setConfirmPassword] = useState(false);
    const handleToggleConfirmPassword = () => {
        setConfirmPassword((prevShowPassword) => !prevShowPassword);
    };

    const [errors, setErrors] = useState({
        name: '',
        role_id: '',
        branch_id: '',
        email: '',
        password_confirmation: ''
    });

    useEffect(() => {
        if (user) {
            console.log("Get user data", user)
            const branchId = user.branch?.id ? user.branch.id.toString() : '';
            const roleId = user.roles?.id ? user.roles.id.toString() : '';
            setFormData({
                id: user.id,
                name: user.name || '',
                user_image: user.user_image || '',
                phone: user.phone || '',
                email: user.email || '',
                role_id: roleId,
                password: user.password || '',
                password_confirmation: user.password_confirmation || '',
                branch_id: branchId,
                status: user.status || 'active',
            });
            // Pre-populate branches with user's branch if not present
            const userBranch = user.branch;
            if (userBranch && !branches.some(b => b.value === userBranch.id.toString())) {
                setBranches(prev => [
                    { value: userBranch.id.toString(), label: userBranch.name },
                    ...prev.filter(b => b.value !== '')
                ]);
            }
            const userRole = user.roles;
            if (userRole && !roles.some(r => r.value === userRole.id.toString())) {
                setRoles(prev => [
                    { value: userRole.id.toString(), label: userRole.name },
                    ...prev.filter(r => r.value !== '')
                ]);
            }
            if (user.user_image) {
                setImagePreview(user.user_image);
            }
        } else {
            setFormData({
                name: '',
                user_image: '',
                phone: '',
                email: '',
                role_id: '',
                password: '',
                password_confirmation: '',
                branch_id: '',
                status: 'active'
            });
            setImagePreview(null);
            setImageFile(null);
        }
        setErrors({
            name: '',
            role_id: '',
            branch_id: '',
            email: '',
            password_confirmation: ''
        });
    }, [user, branches]);

    const branchesRef = React.useRef(false);
    const isMounted = React.useRef(true);

    const loadBranches = useCallback(async (force = false) => {
        // Return if branches are already loaded and no force refresh is requested
        if (branchesRef.current && !force) return;

        try {
            const apiData = await apiClient.get('/get-all-branch');
            if (!isMounted.current) return;
            if (apiData.success && Array.isArray(apiData.data)) {
                const formattedBranches = [
                    { value: '', label: 'Choose' },
                    ...apiData.data.map(branch => ({
                        value: branch.id || branch.name,
                        label: branch.name
                    }))
                ];
                setBranches(formattedBranches);
                branchesRef.current = true;
            } else {
                showToast('Failed to fetch branches', 'error', 'Branch Fetch Operation');
            }
        } catch (error) {
            if (isMounted.current) {
                showToast('Error fetching branches: Please contact the support team.', 'error', 'Branch Fetch Operation');
            }
        }
    }, [showToast]);

    const rolesRef = React.useRef(false);

    const loadRoles = useCallback(async (force = false) => {
        // Return if roles are already loaded and no force refresh is requested
        if (rolesRef.current && !force) return;

        try {
            const apiData = await apiClient.get('/get-all-permission');
            if (!isMounted.current) return;
            if (apiData.success && Array.isArray(apiData.data)) {
                const formattedRoles = [
                    { value: '', label: 'Choose' },
                    ...apiData.data.map(role => ({
                        value: role.id,
                        label: role.roles
                    }))
                ];
                setRoles(formattedRoles);
                rolesRef.current = true;
            } else {
                showToast('Failed to fetch roles', 'error', 'Role Fetch Operation');
            }
        } catch (error) {
            if (isMounted.current) {
                showToast('Error fetching roles: Please contact the support team.', 'error', 'Role Fetch Operation');
            }
        }
    }, [showToast]);

    useEffect(() => {
        loadBranches();
        loadRoles();
        return () => {
            isMounted.current = false;
            branchesRef.current = false;
            rolesRef.current = false;
        };
    }, [loadBranches, loadRoles]);

    AddUsers.propTypes = {
        user: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            user_image: PropTypes.string,
            phone: PropTypes.string,
            email: PropTypes.string,
            password: PropTypes.string,
            password_confirmation: PropTypes.string,
            status: PropTypes.oneOf(['active', 'inactive']),
            branch: PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                name: PropTypes.string
            }),
            roles: PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                name: PropTypes.string
            })
        }),
        onSave: PropTypes.func,
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() ? '' : 'User name is required';
            case 'role_id':
                return value ? '' : 'Role is required';
            case 'branch_id':
                return value ? '' : 'Branch is required';
            case 'email':
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Invalid email format';
            case 'password_confirmation':
                return value === formData.password ? '' : 'Passwords do not match';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {
            name: validateField('name', formData.name),
            role_id: validateField('role_id', formData.role_id),
            branch_id: validateField('branch_id', formData.branch_id),
            email: validateField('email', formData.email),
            password_confirmation: validateField('password_confirmation', formData.password_confirmation)
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error !== '');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData(prev => ({ ...prev, user_image: reader.result }));
            };
            reader.readAsDataURL(file);
            setImageFile(file);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview(null);
        setImageFile(null);
        setFormData(prev => ({ ...prev, user_image: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            try {
                const formDataToSend = new FormData();
                
                // Get user type from localStorage
                const userType = localStorage.getItem('user_type');
                const businessId = localStorage.getItem('business_id');
                
                // Append all form fields
                formDataToSend.append('name', formData.name || '');
                formDataToSend.append('phone', formData.phone || '');
                formDataToSend.append('email', formData.email || '');
                formDataToSend.append('role_id', formData.role_id || '');
                formDataToSend.append('password', formData.password || '');
                formDataToSend.append('password_confirmation', formData.password_confirmation || '');
                formDataToSend.append('branch_id', formData.branch_id || '');
                formDataToSend.append('status', formData.status || 'active');
                
                // Get the selected role's label for user_type
                const selectedRole = roles.find(role => role.value === formData.role_id);
                const userTypeValue = selectedRole.label;
            console.log("Get user type", userTypeValue);
            formDataToSend.append('user_type', userTypeValue);
                // formDataToSend.append('user_type', selectedRole ? selectedRole.label : '');
                // Conditionally append business_id or user_type based on stored user type
                if (userType === 'owner') {
                    formDataToSend.append('business_id', businessId);
                } 
                
                // Handle file upload
                if (imageFile) {
                    formDataToSend.append('user_image', imageFile, imageFile.name);
                }
                
                // Handle ID for updates
                if (formData.id) {
                    formDataToSend.append('id', formData.id);
                }

            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}: ${value}`);
            }

                const Apidata = await apiClient.post('/store-update-business-user', formDataToSend, true);
                
                if (Apidata.success) {
                    onSave && onSave();
                    // Reset form
                    setFormData({ name: '', user_image: '', phone: '', email:'', role_id:'', password: '', password_confirmation:'', descriptions:'', branch_id:'', status:''});
                    setImagePreview(null);
                    setImageFile(null);
                    setErrors({
                        name: '',
                        role: '',
                        branch_id: '',
                        email: '',
                        password_confirmation: ''
                    });
                    document.getElementById('add-units').querySelector('[data-bs-dismiss="modal"]').click()
                    showToast(Apidata.message, 'success', 'Business User Operation!');
                } else {
                    showToast(Apidata.message || 'Failed to save user', 'error', 'Business Users Operation!');
                }
            }
            catch (error) {
                showToast(error.message || 'An unexpected error occurred: Please contact the support team.', 'error', 'Business Users Operation!');
            }
        }
    };
  
    return (
        <div>
            {/* Add User */}
            <div className="modal fade" id="add-units">
                <div className="modal-dialog modal-dialog-centered custom-modal-two">
                    <div className="modal-content">
                        <div className="page-wrapper-new p-0">
                            <div className="content">
                                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                                    <div className="page-title">
                                        <h4>{user ? 'Update User' : 'Add User'}</h4>
                                    </div>
                                    <button
                                        type="button"
                                        className="close"
                                        data-bs-dismiss="modal"
                                        aria-label="Close"
                                    >
                                        <span aria-hidden="true">×</span>
                                    </button>
                                </div>
                                <div className="modal-body custom-modal-body">
                                    <form>
                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div className="new-employee-field">
                                                    <span>Avatar</span>
                                                    <div className="profile-pic-upload mb-2">
                                                        <div className="profile-pic">
                                                            {imagePreview ? (
                                                                <>
                                                                    <img
                                                                        src={imagePreview}
                                                                        alt="Profile Preview"
                                                                        className="profile-preview"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleRemoveImage}
                                                                        className="remove-image-btn"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <span>
                                                                    <PlusCircle className="plus-down-add" />
                                                                    Profile Photo
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="input-blocks mb-0">
                                                            <div className="image-upload mb-0">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    onChange={handleImageUpload}
                                                                    key="image_input"
                                                                />
                                                                <div className="image-uploads">
                                                                    <h4>Change Image</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>User Name</label>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        key="name"
                                                    />
                                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Phone</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                        key="phone_input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Email</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        key="email_input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Role</label>
                                                    <Select
                                                        className={`select ${errors.role_id ? 'is-invalid' : ''}`}
                                                        options={roles}
                                                        placeholder="Choose Role"
                                                        value={roles.find(option => option.value === formData.role_id)}
                                                        onChange={(selectedOption) => handleInputChange({
                                                            target: { name: 'role_id', value: selectedOption.value }
                                                        })}
                                                        key="role_select"
                                                    />
                                                    {errors.role_id && <div className="invalid-feedback">{errors.role_id}</div>}
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Branch</label>
                                                    <Select
                                                        className={`select ${errors.branch_id ? 'is-invalid' : ''}`}
                                                        options={branches}
                                                        placeholder="Choose Branch"
                                                        value={branches.find(option => option.value === formData.branch_id)}
                                                        onChange={(selectedOption) => handleInputChange({
                                                            target: { name: 'branch_id', value: selectedOption.value }
                                                        })}
                                                        key="branch_select"
                                                    />
                                                    {errors.branch_id && <div className="invalid-feedback">{errors.branch_id}</div>}
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Password</label>
                                                    <div className="pass-group">
                                                        <input
                                                            type={showPassword ? 'text' : 'password'}
                                                            className="pass-input"
                                                            placeholder="Enter your password"
                                                            name="password"
                                                            value={formData.password}
                                                            onChange={handleInputChange}
                                                            key="password_input"
                                                        />
                                                        <span
                                                            className={`fas toggle-password ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                                                            onClick={handleTogglePassword}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Confirm Password</label>
                                                    <div className="pass-group">
                                                        <input
                                                            type={showConfirmPassword ? 'text' : 'password'}
                                                            className="pass-input"
                                                            placeholder="Enter your password"
                                                            name="password_confirmation"
                                                            value={formData.password_confirmation}
                                                            onChange={handleInputChange}
                                                            key="password_confirmation"
                                                        />
                                                        <span
                                                            className={`fas toggle-password ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                                                            onClick={handleToggleConfirmPassword}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-lg-6">
                                                <div className="input-blocks">
                                                    <label>Status</label>
                                                    <select className={`form-select ${errors.status ? 'is-invalid' : ''}`}
                                                        name="status" value={formData.status} onChange={handleInputChange}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="modal-footer-btn">
                                            <button
                                                type="button"
                                                className="btn btn-cancel me-2"
                                                data-bs-dismiss="modal"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-submit"
                                                onClick={handleSubmit}
                                            >
                                                {user ? 'Update' : 'Submit'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Add User */}
        </div>
    )
}

AddUsers.defaultProps = {
    user: null,
};
